import React from "react";

import type {
  OfferQuantityButtonType,
  QuantityButtonsVariant,
} from "@intelligems/ig-types";
import { styled } from "@mui/material";
import { getFontWeightAndStyle } from "@src/formatters";
import { useQuantityButtonsContext } from "@src/QuantityButtonsView/context/QuantityButtonsContext";

export const Subtitle = styled("div")<{ hasSubtitle: boolean }>(({
  hasSubtitle,
}) => {
  const { widget } = useQuantityButtonsContext();

  const fontSize = widget.styles.typography.subtitle.fontSize;
  const color = widget.styles.textColors.subtitle;
  const { fontWeight, fontStyle } = getFontWeightAndStyle(
    widget.styles.typography.subtitle.fontStyle
  );

  return {
    fontSize,
    color: hasSubtitle ? color : "transparent",
    fontStyle,
    fontWeight,
  };
});
export const ComparePrice = styled("div")(() => {
  const { widget } = useQuantityButtonsContext();

  const fontSize = widget.styles.typography.comparePrice.fontSize;
  const color = widget.styles.textColors.comparePrice;
  const { fontWeight, fontStyle } = getFontWeightAndStyle(
    widget.styles.typography.comparePrice.fontStyle
  );

  return {
    fontSize,
    color,
    fontWeight,
    fontStyle,
    textDecoration: "line-through",
  };
});

export const FreeLabel = styled("div")<{
  hasLabel: boolean;
  labelType: "freeShipping" | "freeGift";
}>(({ hasLabel, labelType }) => {
  const { widget } = useQuantityButtonsContext();

  const fontSize =
    widget.styles.typography[
      labelType === "freeGift" ? "freeGiftLabel" : "freeShippingLabel"
    ].fontSize;
  const color =
    widget.styles.textColors[
      labelType === "freeGift" ? "freeGiftLabel" : "freeShippingLabel"
    ];

  const backgroundColor =
    widget.styles.backgroundColors[
      labelType === "freeGift" ? "freeGiftBackground" : "freeShippingBackground"
    ];
  const { fontWeight, fontStyle } = getFontWeightAndStyle(
    widget.styles.typography[
      labelType === "freeGift" ? "freeGiftLabel" : "freeShippingLabel"
    ].fontStyle
  );

  return {
    fontSize,
    color: hasLabel ? color : "transparent",
    fontWeight,
    padding: "3px 4px",
    backgroundColor: hasLabel ? backgroundColor : "transparent",
    borderRadius: "4px",
    fontStyle,
  };
});

export const Label = styled("div")<{ hasLabel: boolean }>(({ hasLabel }) => {
  const { widget } = useQuantityButtonsContext();
  const { fontWeight, fontStyle } = getFontWeightAndStyle(
    widget.styles.typography.label.fontStyle
  );
  const fontSize = widget.styles.typography.label.fontSize;
  const color = widget.styles.textColors.label;
  return {
    fontSize,
    color: hasLabel ? color : "transparent",
    fontStyle,
    fontWeight,
  };
});

export const Price = styled("div")(() => {
  const { widget } = useQuantityButtonsContext();

  const fontSize = widget.styles.typography.price.fontSize;
  const color = widget.styles.textColors.price;
  const { fontWeight, fontStyle } = getFontWeightAndStyle(
    widget.styles.typography.price.fontStyle
  );
  return {
    fontSize,
    color,
    fontWeight,
    fontStyle,
  };
});

export const TitleSection = styled("div")(() => {
  const { widget } = useQuantityButtonsContext();
  return {
    flexDirection: widget.styles.appearance.layout === "row" ? "column" : "row",
    display: "flex",
    alignItems:
      widget.styles.appearance.layout === "row" &&
      widget.variant === "quantityButtonsWidgetCompact"
        ? "flex-start"
        : "center",
    gap: widget.styles.appearance.layout === "row" ? "4px" : "10px",
  };
});

export const ButtonTitle = styled("div")(() => {
  const { widget } = useQuantityButtonsContext();
  const { fontWeight, fontStyle } = getFontWeightAndStyle(
    widget.styles.typography.buttonTitle.fontStyle
  );

  const fontSize = widget.styles.typography.buttonTitle.fontSize;
  const color = widget.styles.textColors.buttonTitle;

  return {
    fontSize,
    fontWeight,
    color,
    fontStyle,
  };
});

export const SectionTitle = () => {
  const { widget } = useQuantityButtonsContext();
  const { fontWeight, fontStyle } = getFontWeightAndStyle(
    widget.styles.typography.sectionTitle.fontStyle
  );

  const fontSize = widget.styles.typography.sectionTitle.fontSize;
  const color = widget.styles.textColors.sectionTitle;

  return (
    <div
      className={"ig-quantity-section-title"}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        // width: "100%",
        // maxWidth: "350px",
      }}
    >
      <div style={{ height: "1px", flex: 1, backgroundColor: color }}></div>
      <div
        style={{
          fontSize,
          fontWeight,
          color,
          fontStyle,
          maxWidth: "250px",
        }}
      >
        {widget.variables?.sectionTitle}
      </div>
      <div style={{ height: "1px", flex: 1, backgroundColor: color }}></div>
    </div>
  );
};

export const Badge = styled("div")(() => {
  const { widget } = useQuantityButtonsContext();
  const { styles } = widget;
  const fontSize = styles.typography.badge.fontSize;

  const { fontWeight, fontStyle } = getFontWeightAndStyle(
    widget.styles.typography.subtitle.fontStyle
  );
  const backgroundColor = styles.backgroundColors.badgeColor;
  const color = styles.textColors.badge;

  if (styles.appearance.layout === "row") {
    return {
      position: "absolute",
      top: "0px",
      right: "0px",
      left: "0px",
      backgroundColor,
      color,
      padding: "5px 8px",
      borderTopLeftRadius: styles.appearance.cornerRadius - 1,
      borderTopRightRadius: styles.appearance.cornerRadius - 1,
      fontSize,
      fontWeight,
      fontStyle,
      zIndex: 10,
    };
  } else {
    return {
      position: "absolute",
      top: "-9px",
      right: "18px",
      backgroundColor,
      color,
      padding: "6px 12px",
      borderBottomLeftRadius: "4px",
      borderBottomRightRadius: "4px",
      fontSize,
      fontWeight,
      fontStyle,
      zIndex: 10,
      whiteSpace: "nowrap",
      "&::before": {
        content: '""',
        position: "absolute",
        top: "0",
        left: "-6px",
        width: "6.5px",
        height: "9px",
        clipPath: "path('M6.53784 0V9H0L6.53784 0Z')",
        background: `linear-gradient(180deg, #000 0%, ${backgroundColor} 100%)`,
      },
      "&::after": {
        content: '""',
        position: "absolute",
        top: "0",
        right: "-6.5px",
        width: "6.5px",
        height: "9px",
        clipPath: "path('M0 0V9H6.53784L0 0Z')",
        background: `linear-gradient(180deg, #000 0%, ${backgroundColor} 100%)`,
      },
    };
  }
});

export const FreeGiftBlockContainer = styled("div")(() => {
  const { widget } = useQuantityButtonsContext();
  const borderRadius = widget.styles.appearance.cornerRadius;
  return {
    display: "flex",
    flexDirection: "row",
    backgroundColor: widget.styles.backgroundColors.freeGiftBackground,
    height: "auto",
    borderBottomLeftRadius: borderRadius - 1,
    borderBottomRightRadius: borderRadius - 1,
    padding: "14px 12px",
    position: "relative",
    alignItems: "center",
    gap: "14px",
    justifyContent: "space-between",
  };
});

export const FreeGiftBlockLabel = styled("div")(() => {
  const { widget } = useQuantityButtonsContext();
  const { fontWeight, fontStyle } = getFontWeightAndStyle(
    widget.styles.typography.freeGiftLabel?.fontStyle
  );

  const fontSize = widget.styles.typography.freeGiftLabel.fontSize;
  const color = widget.styles.textColors.freeGiftLabel;

  return {
    color,
    fontStyle,
    fontWeight,
    fontSize,
  };
});

export const ContentIconWrapper = styled("div")(() => {
  const { widget } = useQuantityButtonsContext();
  return {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    flexDirection: widget.styles.appearance.layout === "row" ? "column" : "row",
  };
});

export const QuantityButtonWidgetVariantWrapper = ({
  buttonVariant,
  children,
}: {
  buttonVariant: QuantityButtonsVariant;
  children?: React.ReactNode;
}) => {
  const { widget } = useQuantityButtonsContext();

  return (
    <div
      className={"ig-quantity-button-container-wrapper"}
      style={{
        display: "flex",
        flexDirection: "column",
        textAlign:
          buttonVariant === "quantityButtonsWidgetCompact" &&
          widget.styles.appearance.layout === "row"
            ? "left"
            : "center",
        width: "100%",
      }}
    >
      {children}
    </div>
  );
};

export const SharedButtonLineContent = styled("div")(() => {
  const { widget } = useQuantityButtonsContext();
  return {
    display: "flex",
    flexDirection: widget.styles.appearance.layout === "row" ? "column" : "row",
    alignItems: "center",
    justifyContent: "space-between",
  };
});

export const SharedContentSection = styled("div")({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  gap: "2px",
});

export const SharedButtonContent = styled("div")(() => {
  const { widget } = useQuantityButtonsContext();
  return {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    position: "relative",
    ...(widget.styles.appearance.layout === "row" && {
      marginTop: "16px",
    }),
  };
});

export const SharedRowLayout = ({
  button,
  newTotalPrice,
  renderLabelPlaceholder,
  renderFreeGiftLabelPlaceholder,
  renderFreeShippingLabelPlaceholder,
  totalComparePrice,
  selected,
}: {
  button: OfferQuantityButtonType;
  newTotalPrice: string | undefined;
  renderLabelPlaceholder: boolean;
  renderFreeShippingLabelPlaceholder: boolean;
  renderFreeGiftLabelPlaceholder: boolean;
  totalComparePrice: string | undefined;
  selected: boolean;
}) => {
  return (
    <SharedContentSection style={{ gap: "12px" }}>
      <SharedButtonLineContent>
        <TitleSection>
          <ButtonTitle>
            {button.buttonTitle ||
              `${button.quantity} ${button.unitName || "item"}`}
          </ButtonTitle>
          {!renderLabelPlaceholder ? null : (
            <Label hasLabel={!!button.label}>{button.label || "."}</Label>
          )}
          <Subtitle hasSubtitle={!!button.subtitle}>
            {button?.subtitle || "."}
          </Subtitle>
        </TitleSection>
      </SharedButtonLineContent>

      {!renderFreeShippingLabelPlaceholder &&
      !renderFreeGiftLabelPlaceholder ? null : (
        <SharedButtonLineContent style={{ gap: "4px" }}>
          {!button.freeShipping?.freeShippingLabel ||
          button.freeShipping?.hide ||
          (button.freeShipping?.showOnlyWhenButtonSelected &&
            !selected) ? null : (
            <FreeLabel
              labelType={"freeShipping"}
              hasLabel={!!button.freeShipping?.freeShippingLabel}
            >
              {button.freeShipping?.freeShippingLabel || "."}
            </FreeLabel>
          )}

          {!button.freeGift?.freeGiftLabel ||
          button.freeGift?.hide ||
          (button.freeGift?.showOnlyWhenButtonSelected && !selected) ? null : (
            <FreeLabel
              labelType={"freeGift"}
              hasLabel={!!button.freeGift?.freeGiftLabel}
            >
              {button.freeGift?.freeGiftLabel || "."}
            </FreeLabel>
          )}
        </SharedButtonLineContent>
      )}

      <SharedButtonLineContent>
        <Price>{newTotalPrice}</Price>
        {button.showCompareAtPrice && totalComparePrice && (
          <ComparePrice>{totalComparePrice}</ComparePrice>
        )}
      </SharedButtonLineContent>
    </SharedContentSection>
  );
};

export const SharedColumnLayout = ({
  button,
  newTotalPrice,
  totalComparePrice,
  selected,
}: {
  button: OfferQuantityButtonType;
  newTotalPrice: string | undefined;
  totalComparePrice: string | undefined;
  selected: boolean;
}) => {
  return (
    // <SharedContentSection id={"content-section"}>
    //   <SharedButtonLineContent>
    //     <TitleSection>
    //       <ButtonTitle>
    //         {button.buttonTitle ||
    //           `${button.quantity} ${button.unitName || "item"}`}
    //       </ButtonTitle>
    //       <Label hasLabel={!!button.label}>{button.label || "."}</Label>
    //     </TitleSection>
    //     <Price>{newTotalPrice}</Price>
    //   </SharedButtonLineContent>
    //   {!button.subtitle && !button.showCompareAtPrice ? null : (
    //     <SharedButtonLineContent>
    //       <Subtitle hasSubtitle={!!button.subtitle}>
    //         {button?.subtitle || "."}
    //       </Subtitle>
    //
    //       {button.showCompareAtPrice && totalComparePrice && (
    //         <ComparePrice>{totalComparePrice}</ComparePrice>
    //       )}
    //     </SharedButtonLineContent>
    //   )}
    //
    //   {!button.freeShipping?.freeShippingLabel ||
    //   button.freeShipping?.hide ||
    //   (button.freeShipping?.showOnlyWhenButtonSelected && !selected) ? null : (
    //     <SharedButtonLineContent
    //       style={{ gap: "4px", justifyContent: "flex-start" }}
    //     >
    //       <FreeLabel
    //         labelType={"freeShipping"}
    //         hasLabel={!!button.freeShipping?.freeShippingLabel}
    //       >
    //         {button.freeShipping?.freeShippingLabel || "."}
    //       </FreeLabel>
    //     </SharedButtonLineContent>
    //   )}
    // </SharedContentSection>

    <div
      style={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        width: "100%",
        alignItems: "center",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "2px",
        }}
      >
        <TitleSection>
          <ButtonTitle>
            {button.buttonTitle ||
              `${button.quantity} ${button.unitName || "item"}`}
          </ButtonTitle>
          <Label hasLabel={!!button.label}>{button.label || "."}</Label>
        </TitleSection>
        {!!button.subtitle && (
          <Subtitle hasSubtitle={!!button.subtitle}>
            {button?.subtitle || "."}
          </Subtitle>
        )}
        {!button.freeShipping?.freeShippingLabel ||
        button.freeShipping?.hide ||
        (button.freeShipping?.showOnlyWhenButtonSelected &&
          !selected) ? null : (
          <SharedButtonLineContent
            style={{ gap: "4px", justifyContent: "flex-start" }}
          >
            <FreeLabel
              labelType={"freeShipping"}
              hasLabel={!!button.freeShipping?.freeShippingLabel}
            >
              {button.freeShipping?.freeShippingLabel || "."}
            </FreeLabel>
          </SharedButtonLineContent>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <Price>{newTotalPrice}</Price>
        {button.showCompareAtPrice && totalComparePrice && (
          <ComparePrice>{totalComparePrice}</ComparePrice>
        )}
      </div>
    </div>
  );
};
